import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Dialog, DialogContent, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LocalFireDepartment, RestaurantMenu, TrendingDown } from '@mui/icons-material';
import { apiFetch } from './api';
import { BADGE_COLORS } from './constants';
import { radius } from '../theme';

// Ein eigenes, thematisch passendes Symbol pro Erfolgs-Kategorie statt eines einheitlichen Pokals.
const CATEGORY_META = {
  tracked:       { Icon: RestaurantMenu },
  streak:        { Icon: LocalFireDepartment },
  calorieStreak: { Icon: TrendingDown },
};

// Feste, aber unregelmäßig verteilte Richtungen für den Konfetti-Ausbruch der Freischalt-Animation.
const CONFETTI = [
  { emoji: '🎉', dx: -70, dy: -90, delay: 0 },
  { emoji: '✨', dx: 60, dy: -100, delay: 60 },
  { emoji: '🏅', dx: -100, dy: -30, delay: 120 },
  { emoji: '⭐', dx: 90, dy: -40, delay: 40 },
  { emoji: '🎉', dx: -40, dy: -110, delay: 160 },
  { emoji: '✨', dx: 40, dy: -70, delay: 100 },
  { emoji: '⭐', dx: -80, dy: -70, delay: 20 },
  { emoji: '🏅', dx: 100, dy: -80, delay: 140 },
];

const MedalCircle = ({ tier, unlocked, icon, size = 68 }) => {
  const color = BADGE_COLORS[tier];
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0, mx: 'auto',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: '3px solid', borderColor: unlocked ? color : 'divider',
      bgcolor: unlocked ? color : 'action.disabledBackground',
      color: unlocked ? 'common.white' : 'text.disabled',
      boxShadow: unlocked ? `0 4px 14px ${alpha(color, 0.4)}` : 'none',
      transition: 'all 0.2s',
    }}>
      {icon}
    </Box>
  );
};

const UnlockCelebration = ({ item, onClose }) => {
  if (!item) return null;
  const color = BADGE_COLORS[item.tier];
  const CategoryIcon = CATEGORY_META[item.category].Icon;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { textAlign: 'center', p: 3, overflow: 'visible', position: 'relative' } } }}
    >
      {CONFETTI.map((c, i) => (
        <Box key={i} sx={{
          position: 'absolute', top: '38%', left: '50%', fontSize: 22, pointerEvents: 'none',
          animation: 'nbnh-confetti-burst 1000ms ease-out forwards',
          animationDelay: `${c.delay}ms`,
          opacity: 0,
          '--dx': `${c.dx}px`, '--dy': `${c.dy}px`,
          '@keyframes nbnh-confetti-burst': {
            '0%':   { transform: 'translate(-50%, -50%) scale(0.4)', opacity: 0 },
            '15%':  { opacity: 1 },
            '100%': { transform: 'translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1)', opacity: 0 },
          },
        }}>
          {c.emoji}
        </Box>
      ))}

      <Box sx={{
        width: 110, height: 110, borderRadius: '50%', mx: 'auto', mb: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: color, color: 'common.white',
        boxShadow: `0 0 0 8px ${alpha(color, 0.2)}, 0 8px 24px ${alpha(color, 0.53)}`,
        animation: 'nbnh-medal-pop 600ms cubic-bezier(.34,1.56,.64,1)',
        '@keyframes nbnh-medal-pop': {
          '0%':   { transform: 'scale(0) rotate(-45deg)', opacity: 0 },
          '60%':  { transform: 'scale(1.15) rotate(8deg)', opacity: 1 },
          '100%': { transform: 'scale(1) rotate(0)', opacity: 1 },
        },
      }}>
        <CategoryIcon sx={{ fontSize: 56 }} />
      </Box>
      <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700 }}>
        Neue Medaille!
      </Typography>
      <Typography variant="h6" fontWeight="bold">{item.tier} · {item.label}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Ab {item.threshold} erreicht
      </Typography>
      <Button variant="contained" onClick={onClose}>
        Nice!
      </Button>
    </Dialog>
  );
};

export default function Achievements() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const acked = useRef(false);

  const load = useCallback(async () => {
    const res = await apiFetch('/api/challenges/achievements');
    if (!res.ok) return;
    const json = await res.json();
    setData(json);

    const newOnes = [];
    Object.entries(json).forEach(([category, cat]) => {
      cat.tiers.forEach((t) => {
        if (t.isNew) newOnes.push({ category, label: cat.label, ...t });
      });
    });
    if (newOnes.length > 0) setCelebrationQueue(newOnes);
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismissCelebration = async () => {
    const rest = celebrationQueue.slice(1);
    setCelebrationQueue(rest);
    if (rest.length === 0 && !acked.current) {
      acked.current = true;
      await apiFetch('/api/challenges/achievements/ack', { method: 'POST' });
    }
  };

  if (!data) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {Object.entries(data).map(([category, cat]) => {
        const CategoryIcon = CATEGORY_META[category].Icon;
        return (
          <Box key={category} sx={{ border: 1, borderColor: 'divider', borderRadius: radius.xl, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CategoryIcon fontSize="small" />
              <Typography variant="body2" fontWeight={700}>{cat.label}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                Bestwert: {cat.value}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {cat.tiers.map((t) => (
                <Box
                  key={t.tier}
                  onClick={() => setDetail({ category, label: cat.label, value: cat.value, ...t })}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                >
                  <MedalCircle tier={t.tier} unlocked={t.unlocked} icon={<CategoryIcon fontSize="small" />} />
                  <Typography variant="caption" color={t.unlocked ? 'text.primary' : 'text.disabled'}>
                    {t.tier}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}

      {/* Detail-Dialog */}
      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="xs" fullWidth>
        {detail && (
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <MedalCircle
              tier={detail.tier} unlocked={detail.unlocked} size={100}
              icon={React.createElement(CATEGORY_META[detail.category].Icon, { sx: { fontSize: 44 } })}
            />
            <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>{detail.tier} · {detail.label}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {detail.unlocked
                ? `Erreicht am ${new Date(detail.unlockedAt).toLocaleDateString('de-DE')}`
                : `Noch ${Math.max(0, detail.threshold - detail.value)} bis zum Ziel (${detail.threshold})`}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Bisheriger Bestwert: {detail.value}
            </Typography>
            <Button variant="contained" onClick={() => setDetail(null)} sx={{ mt: 3 }}>
              Schließen
            </Button>
          </DialogContent>
        )}
      </Dialog>

      <UnlockCelebration item={celebrationQueue[0]} onClose={dismissCelebration} />
    </Box>
  );
}
